import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { verifyService } from "../services/verifyServices";
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";

const ConfirmarEmail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    // Recupera o e-mail da navegação, se disponível
    if (location.state && location.state.email) {
      setEmail(location.state.email);
    }
  }, [location]);

  useEffect(() => {
    // Gerencia o contador regressivo para reenvio
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setIsLoading(true);

    try {
      const response = await verifyService.verifyEmail({ email, code });
      if (response.status === 200) {
        setSuccess("E-mail verificado com sucesso!");
        navigate("/", {
          state: {
            verified: true,
            message:
              "Seu e-mail foi verificado com sucesso. Agora você pode fazer login.",
          },
        });
      }
    } catch (error) {
      if (error.response) {
        const status = error.response.status;
        const responseData = error.response.data;

        switch (status) {
          case 400:
            setError(responseData.message || "Código inválido ou expirado");
            break;
          case 500:
            setError("Erro interno do servidor. Tente novamente mais tarde.");
            break;
          default:
            setError("Erro ao verificar o código. Tente novamente.");
        }
      } else {
        setError("Erro de conexão. Verifique sua internet e tente novamente.");
      }
      console.error("Erro na verificação:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError("");
    setSuccess("");
    setIsLoading(true);

    try {
      const response = await verifyService.resendVerificationCode(email);

      if (response.data && response.data.success) {
        setSuccess("Um novo código foi enviado para o seu e-mail!");
        setCountdown(60); // Inicia contador de 60 segundos para reenvio
      }
    } catch (error) {
      if (error.response) {
        const responseData = error.response.data;
        setError(responseData.message || "Erro ao reenviar o código");
      } else {
        setError("Erro de conexão. Verifique sua internet e tente novamente.");
      }
      console.error("Erro ao reenviar código:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <EmailIcon color="primary" sx={{ fontSize: 60, mb: 2 }} />

        <Typography component="h1" variant="h5" gutterBottom>
          Confirme seu e-mail
        </Typography>

        <Typography
          variant="body1"
          align="center"
          color="text.secondary"
          paragraph
        >
          Enviamos um código de verificação para <strong>{email}</strong>. Por
          favor, verifique sua caixa de entrada e insira o código abaixo.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ width: "100%", mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={handleVerifyCode}
          sx={{ mt: 1, width: "100%" }}
        >
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="E-mail"
            name="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            name="code"
            label="Código de verificação"
            id="code"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={isLoading}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={isLoading || !email || !code}
          >
            {isLoading ? <CircularProgress size={24} /> : "Verificar"}
          </Button>

          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button
              variant="text"
              onClick={handleResendCode}
              disabled={isLoading || countdown > 0}
            >
              {countdown > 0
                ? `Reenviar código (${countdown}s)`
                : "Não recebeu? Reenviar código"}
            </Button>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Button
              variant="text"
              onClick={() => navigate("/login")}
              disabled={isLoading}
            >
              Voltar para o login
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default ConfirmarEmail;
